import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Divider from "@mui/joy/Divider";
import IconButton from "@mui/joy/IconButton";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton, { listItemButtonClasses } from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import GlobalStyles from "@mui/joy/GlobalStyles";

import TraficaLogo from "../../assets/images/trafica_logo.png";
import ColorSchemeToggle from "./ColorSchemeToggle";
import { closeSidebar } from "../utils";
import { getUserProfile, removeToken } from "../jwt";
import { useSidebarItems, SidebarItem } from "./SidebarItems";

interface SidebarItemProps {
  item: SidebarItem;
  level?: number;
}

const SidebarItemComponent = ({ item, level = 0 }: SidebarItemProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === item.path;

  const handleClick = () => {
    // Add timestamp to state to force remounting and cache bust
    navigate(item.path, { state: { refresh: Date.now() } });
  };

  return (
    <ListItem>
      <ListItemButton selected={isActive} onClick={handleClick}>
        <ListItemDecorator>{item.icon}</ListItemDecorator>
        <ListItemContent>
          <Typography level="title-sm">{item.title}</Typography>
        </ListItemContent>
      </ListItemButton>
    </ListItem>
  );
};

const Sidebar = () => {
  const userProfile = getUserProfile();
  const sidebarItems = useSidebarItems();

  const handleLogout = () => {
    removeToken();
    window.location.href = "/";
  };

  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: "fixed", md: "sticky" },
        transform: {
          xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
          md: "none",
        },
        transition: "transform 0.4s, width 0.4s",
        zIndex: 10000,
        height: "100dvh",
        width: "var(--Sidebar-width)",
        top: 0,
        p: 2,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          ":root": {
            "--Sidebar-width": "220px",
            [theme.breakpoints.up("lg")]: {
              "--Sidebar-width": "240px",
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: "fixed",
          zIndex: 9998,
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          opacity: "var(--SideNavigation-slideIn)",
          backgroundColor: "var(--joy-palette-background-backdrop)",
          transition: "opacity 0.4s",
          transform: {
            xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))",
            lg: "translateX(-100%)",
          },
        }}
        onClick={() => closeSidebar()}
      />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <IconButton variant="soft" color="primary" size="sm">
          <img
            src={TraficaLogo}
            alt="Trafica Logo"
            style={{ width: 32, height: "auto", margin: "0 auto" }}
          />
        </IconButton>
        <Typography level="title-lg">Trafica</Typography>
        <ColorSchemeToggle sx={{ ml: "auto" }} />
      </Box>
      <Box
        sx={{
          minHeight: 0,
          overflow: "hidden auto",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List
          size="sm"
          sx={{
            gap: 1,
            "--List-nestedInsetStart": "30px",
            "--ListItem-radius": (theme) => theme.vars.radius.sm,
          }}
        >
          {sidebarItems.map((item) => (
            <SidebarItemComponent key={item.path} item={item} />
          ))}
        </List>
      </Box>
      <Divider />
      <Box
        sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}
      >
        <Avatar
          variant="outlined"
          size="sm"
          alt={`${userProfile.firstName} ${userProfile.lastName}`}
        />
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "150px",
          }}
        >
          <Typography level="title-sm" noWrap>
            {userProfile.firstName} {userProfile.lastName}
          </Typography>
          <Typography level="body-xs" noWrap>
            {userProfile.email}
          </Typography>
        </Box>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={handleLogout}
        >
          <LogoutRoundedIcon />
        </IconButton>
      </Box>
    </Sheet>
  );
};

export default Sidebar;
